import { type Component, createMemo, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import type { CardBodyPropsType, CardColorsType, CardFooterPropsType, CardPropsType, CardStyleType, CardTitlePropsType } from '~/types/Card'

const Card: Component<CardPropsType> & {
  Title: Component<CardTitlePropsType>
  Body: Component<CardBodyPropsType>
  Footer: Component<CardFooterPropsType>
  Header: Component<CardTitlePropsType>
} = props => {
  const [local, others] = splitProps(props, ['class', 'variant', 'color', 'style'])
  const variant = local.variant || 'elevation'
  const style: CardStyleType = local.style || 'border'

  const variantClasses = {
    elevation: 'bg-surface border border-border shadow-md transition-shadow duration-150',
    outlined: 'bg-surface border border-border'
  }

  const colorClasses: Record<CardColorsType, string> = {
    primary: 'border-t-primary-500',
    secondary: 'border-t-secondary-500',
    success: 'border-t-success-500',
    danger: 'border-t-danger-500',
    warning: 'border-t-warning-500',
    info: 'border-t-info-500',
    blue: 'border-t-blue-500',
    purple: 'border-t-purple-500',
    emerald: 'border-t-emerald-500',
    orange: 'border-t-orange-500',
    red: 'border-t-red-500',
    default: ''
  }

  const containerStyles: Record<CardColorsType, string> = {
    primary: 'bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/20 dark:to-slate-800 border border-primary/60',
    secondary: 'bg-gradient-to-br from-secondary-50 to-white dark:from-secondary-900/20 dark:to-slate-800 border border-secondary-200/60 dark:border-secondary-700/60',
    success: 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800 border border-emerald-200/60 dark:border-emerald-700/60',
    danger: 'bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-slate-800 border border-danger-soft/60',
    warning: 'bg-gradient-to-br from-orange-50 to-white dark:from-orange-900/20 dark:to-slate-800 border border-orange-200/60 dark:border-orange-700/60',
    info: 'bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800 border border-blue-200/60 dark:border-blue-700/60',
    blue: 'bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800 border border-blue-200/60 dark:border-blue-700/60',
    purple: 'bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-slate-800 border border-purple-200/60 dark:border-purple-700/60',
    emerald: 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800 border border-emerald-200/60 dark:border-emerald-700/60',
    orange: 'bg-gradient-to-br from-orange-50 to-white dark:from-orange-900/20 dark:to-slate-800 border border-orange-200/60 dark:border-orange-700/60',
    red: 'bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-slate-800 border border-danger-soft/60',
    default: 'bg-surface border border-border'
  }

  const cardClasses = createMemo(() => {
    let baseClasses = ''
    let colorClass = ''

    if (style === 'border' && local.color) {
      baseClasses = variantClasses[variant] ?? ''
      colorClass = colorClasses[local.color]
    } else if (style === 'container' && local.color) {
      baseClasses = 'rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105 cursor-pointer'
      colorClass = containerStyles[local.color]
    } else if (style === 'both' && local.color) {
      baseClasses = 'rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105 cursor-pointer'
      colorClass = `${containerStyles[local.color]} ${colorClasses[local.color]}`
    } else {
      baseClasses = variantClasses[variant] ?? ''
    }

    return twMerge(`w-full rounded-xl overflow-hidden ${baseClasses} ${colorClass}`, local.class)
  })

  return (
    <div {...others} class={cardClasses()}>
      {props.Title && <div class="px-6 py-4 border-b border-border w-full">{props.Title}</div>}
      {props.children}
      {props.Body && <div class="px-6 py-4">{props.Body}</div>}
      {props.Footer && <div class="px-6 py-4 bg-surface-muted/20">{props.Footer}</div>}
    </div>
  )
}

Card.Title = props => {
  const [local, others] = splitProps(props, ['class', 'bold'])

  const titleClasses = createMemo(() => twMerge(`px-6 py-4 border-b border-border w-full ${local.bold ? 'font-medium' : ''}`, local.class))

  return (
    <div {...others} class={titleClasses()}>
      {props.children}
    </div>
  )
}

Card.Body = props => {
  const [local, others] = splitProps(props, ['class'])
  const bodyClasses = createMemo(() => twMerge('w-full px-6 py-4', local.class))
  return (
    <div {...others} class={bodyClasses()}>
      {props.children}
    </div>
  )
}

Card.Footer = props => {
  const [local, others] = splitProps(props, ['class'])
  const footerClasses = createMemo(() => twMerge(`border-t border-border w-full px-6 py-4 bg-surface-muted/20`, local.class))
  return (
    <div {...others} class={footerClasses()}>
      {props.children}
    </div>
  )
}

Card.Header = props => {
  const [local, others] = splitProps(props, ['class'])
  const headerClasses = createMemo(() => twMerge(`px-6 py-4 border-b border-border w-full`, local.class))
  return (
    <div {...others} class={headerClasses()}>
      <div class="flex items-center gap-2">
        {props.icon && <span class="text-xl">{props.icon}</span>}
        <div class="text-left">
          {props.title && <div class="text-lg font-semibold">{props.title}</div>}
          {props.subtitle && <div class="text-sm text-foreground-subtle">{props.subtitle}</div>}
        </div>
      </div>
      {props.children}
    </div>
  )
}

export default Card
